import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, BehaviorSubject, throwError } from 'rxjs';
import { tap, map, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface Region {
  value: string;
  display: string;
}

export interface CurrencyOption {
  value: string;
  display: string;
  selected?: boolean;
}

export interface CompanyCodeOption {
  value: string;
  display: string;
  selected?: boolean;
}
export interface SalesDistribution {
  salesOrganization: string;
  distributionChannel: string;
  distChannelDescription?: string;
}

export interface SalesDistributionOption {
  value: string;
  display: string;
  salesOrg: string;
  distChannelDescription?: string;
}
export interface CompanyCodeApiResponse {
  transactionId: string;
  message: string;
  companyCodes: string[];
}

export interface SalesDistributionResponse {
  transactionId: string;
  message: string;
  salesOrgDistributionsChannels: SalesDistribution[];
}

export interface Material {
  materialNumber: string;
  materialNumberDescriptionLong: string;
  materialNumberDescriptionShort: string;
}

export interface MaterialValidationResponse {
  transactionId: string;
  isExists: boolean;
  message: string;
  materials: Material[];
}

@Injectable({
  providedIn: 'root'
})
export class CompanyCodeService {
  private apiBaseUrl = environment.apiBaseUrl;
  private apiKey = environment.apiKey;

  // API Endpoints
  private companiesEndpoint = '/companycodes';
  private regionsEndpoint = '/regions';
  private currencyEndpoint = '/currencycodes';
  private customersEndpoint = '/customers';
  private salesDistributionEndpoint = '/salesorgdistributions';
  private materialsEndpoint = '/materials';
  private orderReasonsEndpoint = '/orderreasons';

  // Full URLs
  private get apiUrl() { return `${this.apiBaseUrl}${this.companiesEndpoint}`; }
  private get regionsUrl() { return `${this.apiBaseUrl}${this.regionsEndpoint}`; }
  private get currencyUrl() { return `${this.apiBaseUrl}${this.currencyEndpoint}`; }
  private get customersUrl() { return `${this.apiBaseUrl}${this.customersEndpoint}`; }
  private get materialsUrl() { return `${this.apiBaseUrl}${this.materialsEndpoint}`; }
  private get salesDistributionUrl() { return `${this.apiBaseUrl}${this.salesDistributionEndpoint}`; }
  private get orderReasonsUrl() { return `${this.apiBaseUrl}${this.orderReasonsEndpoint}`; }

  private regionsSubject = new BehaviorSubject<Region[]>([]);
  public regions$ = this.regionsSubject.asObservable();

  private regionsLoadingSubject = new BehaviorSubject<boolean>(false);
  public regionsLoading$ = this.regionsLoadingSubject.asObservable();

  private currenciesSubject = new BehaviorSubject<CurrencyOption[]>([]);
  public currencies$ = this.currenciesSubject.asObservable();

  private companyCodesSubject = new BehaviorSubject<CompanyCodeOption[]>([]);
  public companyCodes$ = this.companyCodesSubject.asObservable();

  private selectedCompanyCodeSubject = new BehaviorSubject<string>('');
  public selectedCompanyCode$ = this.selectedCompanyCodeSubject.asObservable();

  private selectedDistributionChannelSubject = new BehaviorSubject<string>('');
  public selectedDistributionChannel$ = this.selectedDistributionChannelSubject.asObservable();

  private salesDistributionsSubject = new BehaviorSubject<SalesDistributionOption[]>([]);
  public salesDistributions$ = this.salesDistributionsSubject.asObservable();

  private customersCache = new Map<string, any>();

  constructor(private http: HttpClient) {}

  /**
   * Map UI region names to API region codes
   * @param regionDisplay - Region display name from UI (e.g., 'DACH', 'UKI Media')
   * @returns API region code (e.g., 'SKDE', 'SKUK') or undefined if no mapping exists
   */
  private mapRegionToApiCode(regionDisplay: string): string | undefined {
    if (!regionDisplay) return undefined;
    
    const normalized = regionDisplay.trim().toLowerCase();
    
    // Map UI region names to API codes
    const regionMap: { [key: string]: string } = {
      'dach': 'SKDE',
      'uki media': 'SKUK',
      'uki non media': 'SKUK',
      'ita': 'SKIT'
    };
    
    const apiCode = regionMap[normalized];
    
    return apiCode;
  }

  private getRegionRestrictedCodes(regionDisplay: string): string | undefined {
    const normalized = regionDisplay.trim().toLowerCase();
    const restrictedCodesMap: { [key: string]: string } = {
      'uki media': '2000,3001'
    };
    return restrictedCodesMap[normalized];
  }

  /**
   * Get company codes from the API
   * @param companyCode - Optional company code filter (e.g., 'UA01')
   * @returns Observable with company codes data
   */
  getCompanyCodes(companyCode?: string): Observable<any> {
    const headers = new HttpHeaders({
      'x-functions-key': this.apiKey
    });

    let params = new HttpParams();
    if (companyCode) {
      params = params.set('companycode', companyCode);
    }

    return this.http.get(this.apiUrl, { headers, params });
  }

  /**
   * Load company codes from API, optionally filtered by region
   * @param region - Optional region display name (e.g., 'DACH', 'UKI Media') to filter company codes
   * @returns Observable with company code options
   */
  loadCompanyCodes(region?: string): Observable<CompanyCodeOption[]> {
    // If region is provided, always fetch fresh data (don't use cache)
    // Otherwise, if already loaded, return cached data
    if (!region && this.companyCodesSubject.value.length > 0) {
      return of(this.companyCodesSubject.value);
    }

    // Clear cache when loading with region filter to ensure fresh data
    if (region) {
      this.companyCodesSubject.next([]);
    }

    const headers = new HttpHeaders({
      'x-functions-key': this.apiKey
    });

    // Build query parameters
    let params = new HttpParams();
    if (region) {
      const apiRegionCode = this.mapRegionToApiCode(region);
      if (apiRegionCode) {
        params = params.set('region', apiRegionCode);
      }
      const restrictedCodes = this.getRegionRestrictedCodes(region);
      if (restrictedCodes) {
        params = params.set('codes', restrictedCodes);
      }
    }

    return this.http.get<CompanyCodeApiResponse>(this.apiUrl, { headers, params }).pipe(
      map(response => {
        if (!response.companyCodes || !Array.isArray(response.companyCodes) || response.companyCodes.length === 0) {
          throw new Error('No company codes found in API response');
        }

        // Map response to CompanyCodeOption format
        const companyCodes: CompanyCodeOption[] = response.companyCodes.map((code: any) => {
          // If code is a string, use it directly for both value and display
          if (typeof code === 'string') {
            return {
              value: code,
              display: code
            };
          }
          // If code is an object with companyCode and businessUnitText
          else if (typeof code === 'object' && code !== null) {
            const companyCode = code.companyCode || code.value || code.code || '';
            const businessUnitText = code.businessUnitText || '';
            
            // Create display format: "companyCode-businessUnitText" if businessUnitText exists
            const display = businessUnitText 
              ? `${companyCode}-${businessUnitText}` 
              : companyCode;
            
            return {
              value: companyCode,
              display: display
            };
          }
          // Fallback for unexpected types
          else {
            return {
              value: String(code),
              display: String(code)
            };
          }
        });

        // Add default "Choose Company Code" option at the beginning
        const companyCodeOptions = [
          { value: '', display: 'Choose Company Code', selected: true },
          ...companyCodes
        ];

        this.companyCodesSubject.next(companyCodeOptions);
        return companyCodeOptions;
      }),
      catchError((error) => {
        console.error('Company codes API error:', error);
        console.error('Error details:', {
          status: error.status,
          statusText: error.statusText,
          message: error.message,
          error: error.error
        });
        return this.handleError(error);
      })
    );
  }

  /**
   * Get all company codes without filter (alias for backward compatibility)
   * @returns Observable with all company codes
   */
  getAllCompanyCodes(): Observable<CompanyCodeOption[]> {
    return this.loadCompanyCodes();
  }

  /**
   * Clear company codes cache (useful when region changes)
   */
  clearCompanyCodesCache(): void {
    this.companyCodesSubject.next([]);
  }

  /**
   * Get cached company codes
   * @returns Array of company code options
   */
  getCompanyCodesSync(): CompanyCodeOption[] {
    return this.companyCodesSubject.value;
  }

  /**
   * Load regions from API (cached after first call)
   * @returns Observable with regions data
   */
  loadRegions(): Observable<Region[]> {
    this.regionsLoadingSubject.next(true);
    // If already loaded, return cached data
    if (this.regionsSubject.value.length > 0) {
      this.regionsLoadingSubject.next(false);
      return of(this.regionsSubject.value);
    }

    const headers = new HttpHeaders({
      'x-functions-key': this.apiKey
    });

    return this.http.get<{ transactionId: string, regions: string[] }>(this.regionsUrl, { headers }).pipe(
      map(response => {
        const regions = response.regions.map(region => ({
          value: region,
          display: region
        }));
        this.regionsSubject.next(regions);
        this.regionsLoadingSubject.next(false);
        return regions;
      }),
      catchError((error) => {
        this.regionsLoadingSubject.next(false);
        return this.handleError(error);
      })
    );
  }

  /**
   * Get cached regions
   * @returns Array of regions
   */
  getRegions(): Region[] {
    return this.regionsSubject.value;
  }

  /**
   * Load currencies from API (cached after first call)
   * @returns Observable with currency options
   */
  loadCurrencies(): Observable<CurrencyOption[]> {
    // If already loaded, return cached data
    if (this.currenciesSubject.value.length > 0) {
      return of(this.currenciesSubject.value);
    }

    const headers = new HttpHeaders({
      'x-functions-key': this.apiKey
    });

    return this.http.get<{ transactionId: string, message: string, currencyCodes: string[] }>(`${this.currencyUrl}?currencyCode=A`, { headers }).pipe(
      map(response => {
        if (!response.currencyCodes || !Array.isArray(response.currencyCodes) || response.currencyCodes.length === 0) {
          console.error('No currency codes found in response:', response);
          throw new Error('No currency codes found in API response');
        }

        const currencies: CurrencyOption[] = response.currencyCodes.map((code: string) => ({
          value: code,
          display: code
        }));

        // Add default "Choose Currency" option at the beginning
        const currencyOptions = [
          { value: '', display: 'Choose Currency', selected: true },
          ...currencies
        ];
        this.currenciesSubject.next(currencyOptions);
        return currencyOptions;
      }),
      catchError((error) => {
        console.error('Currency API error:', error);
        console.error('Error details:', {
          status: error.status,
          statusText: error.statusText,
          message: error.message,
          error: error.error
        });
        return this.handleError(error);
      })
    );
  }

  /**
 * Search currencies by code (without caching)
 * @param currencyCode - Search term (e.g., 'A', 'USD', 'EUR')
 * @returns Observable with filtered currency options
 */
searchCurrencies(currencyCode: string): Observable<CurrencyOption[]> {
  if (!currencyCode || currencyCode.trim().length === 0) {
    currencyCode = 'A'; // Default search
  }

  const headers = new HttpHeaders({
    'x-functions-key': this.apiKey
  });

  return this.http.get<{ transactionId: string, message: string, currencyCodes: string[] }>(
    `${this.currencyUrl}?currencyCode=${currencyCode}`, 
    { headers }
  ).pipe(
    map(response => {
      if (!response.currencyCodes || !Array.isArray(response.currencyCodes)) {
        return [];
      }

      const currencies: CurrencyOption[] = response.currencyCodes.map((code: string) => ({
        value: code,
        display: code
      }));

      return currencies;
    }),
    catchError((error) => {
      console.error('Currency search API error:', error);
      return of([]);
    })
  );
}
  /**
   * Get cached currencies
   * @returns Array of currency options
   */
  getCurrencies(): CurrencyOption[] {
    return this.currenciesSubject.value;
  }

  /**
   * Get customers by company code
   * @param companyCode - Company code (e.g., '5016')
   * @returns Observable with customers data
   */
  getCustomers(companyCode: string): Observable<any> {
    // Check cache first
    if (this.customersCache.has(companyCode)) {
      return of(this.customersCache.get(companyCode));
    }

    const headers = new HttpHeaders({
      'x-functions-key': this.apiKey
    });

    const params = new HttpParams().set('companyCode', companyCode);

    return this.http.get<any>(`${this.customersUrl}`, { headers, params }).pipe(
      tap(response => {
        // Cache the response
        this.customersCache.set(companyCode, response);
      }),
      catchError((error) => {
        console.error('Customers API error:', error);
        console.error('Error details:', {
          status: error.status,
          statusText: error.statusText,
          message: error.message,
          url: error.url
        });
        return this.handleError(error);
      })
    );
  }

  /**
   * Search customers by company code and customer number (without caching)
   * @param companyCode - Company code (e.g., '5016')
   * @param customerNumber - Customer number search term (e.g., '1004')
   * @returns Observable with filtered customer options
   */
  searchCustomers(companyCode: string, customerNumber: string): Observable<any[]> {
    if (!companyCode) {
      console.warn('Cannot search customers: No company code provided');
      return of([]);
    }

    if (!customerNumber || customerNumber.trim().length === 0) {
      return of([]); // Return empty if no search term
    }

    const headers = new HttpHeaders({
      'x-functions-key': this.apiKey
    });

    const params = new HttpParams()
      .set('companyCode', companyCode)
      .set('customerNumber', customerNumber);

    return this.http.get<any>(`${this.customersUrl}`, { headers, params }).pipe(
      map(response => {
        if (!response.customers || !Array.isArray(response.customers)) {
          return [];
        }

        // Transform to options format
        const customerOptions = response.customers.map((customer: any) => ({
          value: customer.customerNumber,
          display: `${customer.customerNumber} - ${customer.name}`,
          data: customer // Keep full customer data for form filling
        }));

        return customerOptions;
      }),
      catchError((error) => {
        console.error('Customer search API error:', error);
        return of([]);
      })
    );
  }

  /**
   * Search customers by company code and customer name (without caching)
   * @param companyCode - Company code (e.g., '5016')
   * @param customerName - Customer name search term (e.g., 'Sky UK Ltd')
   * @returns Observable with filtered customer options
   */
  searchCustomersByName(companyCode: string, customerName: string): Observable<any[]> {
    if (!companyCode) {
      console.warn('Cannot search customers: No company code provided');
      return of([]);
    }

    if (!customerName || customerName.trim().length === 0) {
      return of([]); // Return empty if no search term
    }

    const headers = new HttpHeaders({
      'x-functions-key': this.apiKey
    });

    const params = new HttpParams()
      .set('companyCode', companyCode)
      .set('customerName', customerName);

    return this.http.get<any>(`${this.customersUrl}`, { headers, params }).pipe(
      map(response => {
        if (!response.customers || !Array.isArray(response.customers)) {
          return [];
        }

        // Transform to options format
        const customerOptions = response.customers.map((customer: any) => ({
          value: customer.name,
          display: `${customer.name} - ${customer.customerNumber}`,
          data: customer // Keep full customer data for form filling
        }));

        return customerOptions;
      }),
      catchError((error) => {
        console.error('Customer name search API error:', error);
        return of([]);
      })
    );
  }

  /**
   * Clear all caches (useful for forcing refresh)
   */
  clearAllCaches(): void {
    this.regionsSubject.next([]);
    this.currenciesSubject.next([]);
    this.companyCodesSubject.next([]);
    this.salesDistributionsSubject.next([]);
    this.customersCache.clear();
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An error occurred';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Client Error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = `Server Error: ${error.status} - ${error.message}`;
    }

    console.error('CompanyCodeService Error:', errorMessage);
    return throwError(() => new Error(errorMessage));
  }

  setSelectedCompanyCode(companyCode: string): void {
    this.selectedCompanyCodeSubject.next(companyCode);
  }

  getSelectedCompanyCode(): string {
    return this.selectedCompanyCodeSubject.value;
  }

  setSelectedDistributionChannel(channel: string): void {
    this.selectedDistributionChannelSubject.next(channel);
  }

  getSelectedDistributionChannel(): string {
    return this.selectedDistributionChannelSubject.value;
  }

    /**
   * Load all sales distribution channels from API (cached after first call)
   * @returns Observable with sales distribution options
   */
  loadSalesDistributions(): Observable<SalesDistributionOption[]> {
    // If already loaded, return cached data
    if (this.salesDistributionsSubject.value.length > 0) {
      return of(this.salesDistributionsSubject.value);
    }

    const headers = new HttpHeaders({
      'x-functions-key': this.apiKey
    });

    return this.http.get<SalesDistributionResponse>(this.salesDistributionUrl, { headers }).pipe(
      map(response => {
        if (!response.salesOrgDistributionsChannels || 
            !Array.isArray(response.salesOrgDistributionsChannels) || 
            response.salesOrgDistributionsChannels.length === 0) {
          console.warn('No sales distributions found in response');
          return [];
        }

        // Map to option format
        const salesDistributions: SalesDistributionOption[] = response.salesOrgDistributionsChannels.map((dist: SalesDistribution) => ({
          value: dist.distributionChannel,
          display: dist.distChannelDescription || `${dist.distributionChannel} (Org: ${dist.salesOrganization})`,
          salesOrg: dist.salesOrganization,
          distChannelDescription: dist.distChannelDescription
        }));

        // Update the BehaviorSubject with loaded data
        this.salesDistributionsSubject.next(salesDistributions);
        return salesDistributions;
      }),
      catchError((error) => {
        console.error('Error loading sales distributions:', error);
        console.error('Error details:', {
          status: error.status,
          statusText: error.statusText,
          message: error.message,
          error: error.error
        });
        this.salesDistributionsSubject.next([]);
        return of([]);
      })
    );
  }

  /**
   * Get cached sales distributions
   * @returns Array of sales distribution options
   */
  getSalesDistributions(): SalesDistributionOption[] {
    return this.salesDistributionsSubject.value;
  }

  /**
   * Filter sales distributions by sales organization
   * @param salesOrg - Sales organization code
   * @returns Filtered array of sales distribution options
   */
  filterSalesDistributionsBySalesOrg(salesOrg: string): SalesDistributionOption[] {
    return this.salesDistributionsSubject.value.filter(dist => dist.salesOrg === salesOrg);
  }

  /**
   * Validate SAP material number against the materials API
   * @param materialNumber - Material number to validate
   * @returns Observable with material validation response
   */
  validateMaterialNumber(materialNumber: string, salesOrganization?: string, distributionChannel?: string): Observable<MaterialValidationResponse> {
    if (!materialNumber || materialNumber.trim().length === 0) {
      return of({
        transactionId: '',
        isExists: false,
        message: 'Material number cannot be empty',
        materials: []
      });
    }

    const headers = new HttpHeaders({
      'x-functions-key': this.apiKey
    });

    let params = new HttpParams().set('materialNumber', materialNumber);
    
    // Add optional parameters if provided
    if (salesOrganization) {
      params = params.set('salesorganization', salesOrganization);
    }
    if (distributionChannel) {
      params = params.set('distributionchannel', distributionChannel);
    }

    return this.http.get<MaterialValidationResponse>(`${this.materialsUrl}`, { headers, params }).pipe(
      map(response => {
        return response;
      }),
      catchError((error) => {
        console.error('Material validation API error:', error);
        console.error('Error details:', {
          status: error.status,
          statusText: error.statusText,
          message: error.message,
          url: error.url
        });
        // Return a failure response on API error
        return of({
          transactionId: '',
          isExists: false,
          message: 'Error validating material number. Please try again.',
          materials: []
        });
      })
    );
  }

  getOrderReasons(selectedRegion: string): Observable<any> {
    const headers = new HttpHeaders({
      'x-functions-key': this.apiKey
    });
    let params = new HttpParams().set('region', selectedRegion);
    return this.http.get<any>(this.orderReasonsUrl, { headers, params });
  }
}